import { Loader2, Trash2, UserPlus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RoleBadge } from '@/components/settings/RoleBadge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { CaseMember, GrantableCaseRole } from '@/services/api/cases'
import { cn } from '@/utils/cn'

interface CaseMembersPanelProps {
  members: CaseMember[]
  loading?: boolean
  /** Whether the current user may add/remove/change members (owner or superadmin). */
  canManage: boolean
  adding?: boolean
  onAdd: (username: string, role: GrantableCaseRole) => void
  onChangeRole: (userId: string, role: GrantableCaseRole) => void
  onRemove: (userId: string) => void
}

function GrantableRoleSelect({
  value,
  onChange,
}: {
  value: GrantableCaseRole
  onChange: (role: GrantableCaseRole) => void
}) {
  const { t } = useTranslation()
  return (
    <Select value={value} onValueChange={v => onChange(v as GrantableCaseRole)}>
      <SelectTrigger className="w-36 max-md:h-9! max-md:flex-1">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="viewer">{t('cases.members.viewer')}</SelectItem>
        <SelectItem value="collaborator">{t('cases.members.collaborator')}</SelectItem>
      </SelectContent>
    </Select>
  )
}

export function CaseMembersPanel({
  members,
  loading,
  canManage,
  adding,
  onAdd,
  onChangeRole,
  onRemove,
}: CaseMembersPanelProps) {
  const { t } = useTranslation()
  const [username, setUsername] = useState('')
  const [role, setRole] = useState<GrantableCaseRole>('viewer')

  const submit = () => {
    const value = username.trim()
    if (!value) return
    onAdd(value, role)
    setUsername('')
  }

  return (
    <Card>
      <CardHeader className="max-md:px-5">
        <CardTitle className="text-sm">{t('cases.members.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 max-md:px-5">
        {canManage && (
          <div className="flex flex-wrap items-center gap-2 md:flex-nowrap">
            <Input
              value={username}
              onChange={e => setUsername(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') submit()
              }}
              placeholder={t('cases.members.addPlaceholder')}
            />
            <GrantableRoleSelect value={role} onChange={setRole} />
            <Button onClick={submit} disabled={adding || !username.trim()}>
              {adding ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <UserPlus className="size-4" />
              )}
              {t('cases.members.add')}
            </Button>
          </div>
        )}

        {loading ? (
          <p className="text-sm text-muted-foreground">{t('cases.members.loading')}</p>
        ) : members.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('cases.members.empty')}</p>
        ) : (
          <ul className="divide-y divide-border">
            {members.map(m => (
              <li
                key={m.user_id}
                className="flex flex-wrap items-center gap-x-3 gap-y-2 py-2 md:flex-nowrap md:gap-3"
              >
                {/* Below md a manageable member's name takes the whole line; the
                    role select and remove button sit on the next one. */}
                <div
                  className={cn(
                    'min-w-0 flex-1',
                    canManage && m.role !== 'owner' && 'max-md:basis-full'
                  )}
                >
                  <p className="truncate text-sm font-medium">{m.full_name || m.username}</p>
                  <p className="truncate text-xs text-muted-foreground">{m.email || m.username}</p>
                </div>
                {canManage && m.role !== 'owner' ? (
                  <GrantableRoleSelect value={m.role} onChange={r => onChangeRole(m.user_id, r)} />
                ) : (
                  <RoleBadge role={m.role} />
                )}
                {canManage && m.role !== 'owner' && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onRemove(m.user_id)}
                    aria-label={t('cases.members.removeLabel')}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}
